import React, { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CheckCircle2, UserPlus, ArrowLeft, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { useGps } from "@/features/gps/useGps";
import { GpsIndicator } from "@/features/gps/GpsIndicator";
import { Card, Pill, Button, Field, Input, Spinner, EmptyState } from "@/components/ui";
import { SignaturePad, type SignaturePadHandle } from "@/features/signature/SignaturePad";
import { fmtDateTime } from "@/lib/dates";

// =============================================================================
// JSEA / Prestart team sign-on
// =============================================================================
// Route: /forms/signon/:submissionId
// After the PICW submits a JSEA, workers queue up to sign on. Each sign-on
// is a form_signons row with its own signature SVG, GPS, and device info.
// Minimum count comes from the template.min_sign_on_count column.
// =============================================================================

export default function JseaSignonScreen() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const gps = useGps(true);
  const sigRef = useRef<SignaturePadHandle | null>(null);

  const [signerName, setSignerName] = useState("");
  const [signerCompany, setSignerCompany] = useState("");
  const [signerRole, setSignerRole] = useState("");
  const [pickingMode, setPickingMode] = useState<"self" | "other">("self");
  const [signing, setSigning] = useState(false);

  // -- Load submission + template -------------------------------------------
  const { data: submission, isLoading, refetch } = useQuery({
    queryKey: ["form_submission", submissionId],
    enabled: !!submissionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_submissions")
        .select(`
          submission_id, captured_at, field_values,
          template:form_templates(display_name, template_code, min_sign_on_count),
          project:projects(project_code, project_name),
          site:sites(site_code, site_name)
        `)
        .eq("submission_id", submissionId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // -- Existing sign-ons ----------------------------------------------------
  const { data: signons = [] } = useQuery({
    queryKey: ["form_signons", submissionId],
    enabled: !!submissionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_signons")
        .select(`
          signon_id, signer_name, signer_company, signer_role, signed_at, gps_lat, gps_lng,
          employee_id,
          employee:v_employees(first_name, last_name)
        `)
        .eq("submission_id", submissionId!)
        .order("signed_at", { ascending: true });
      if (error) throw error;
      return data as any[];
    },
  });

  // -- Sign-on mutation -----------------------------------------------------
  const signMut = useMutation({
    mutationFn: async () => {
      if (!submissionId) throw new Error("no_submission");
      const sig = sigRef.current;
      if (!sig || sig.isEmpty()) throw new Error("signature_required");

      const payload: any = {
        submission_id: submissionId,
        signer_name:    pickingMode === "self"
          ? `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || user?.login_email
          : signerName.trim(),
        signer_company: pickingMode === "self" ? "Coretel Group" : signerCompany.trim() || null,
        signer_role:    signerRole.trim() || null,
        employee_id:    pickingMode === "self" ? user?.employee_id ?? null : null,
        signature_svg:  sig.getSvg(),
        gps_lat:        gps.fix?.latitude,
        gps_lng:        gps.fix?.longitude,
        device_info:    { ua: navigator.userAgent, platform: navigator.platform, sig_meta: sig.getMeta() },
        signed_at:      new Date().toISOString(),
      };

      const { error } = await supabase.from("form_signons").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      setSignerName("");
      setSignerCompany("");
      setSignerRole("");
      sigRef.current?.clear();
      refetch();
    },
  });

  // -- Remove sign-on (only the original signer or a manager can) -----------
  const removeMut = useMutation({
    mutationFn: async (signon_id: string) => {
      const { error } = await supabase.from("form_signons").delete().eq("signon_id", signon_id);
      if (error) throw error;
    },
    onSuccess: () => refetch(),
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner size={20} /></div>;
  if (!submission) {
    return (
      <div className="max-w-md mx-auto p-6">
        <Card><EmptyState title="Submission not found" /></Card>
        <div className="mt-4 text-center">
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/forms")}>Back to forms</Button>
        </div>
      </div>
    );
  }

  const template: any = submission.template;
  const minSign = template?.min_sign_on_count ?? 1;
  const metQuota = signons.length >= minSign;

  // Hide self-signon if user has already signed
  const alreadySignedBySelf = user?.employee_id && signons.some(s => s.employee_id === user.employee_id);

  return (
    <div className="max-w-lg mx-auto p-4 pb-32">
      {/* Header */}
      <button onClick={() => navigate("/forms")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Forms
      </button>

      <div className="mb-4">
        <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">Sign-on</div>
        <h1 className="text-[22px] font-semibold tracking-tight mt-1">{template?.display_name ?? "JSEA"}</h1>
        <div className="text-text-muted text-[13px] mt-1">
          {submission.project?.project_code ?? "—"}{submission.project?.project_name ? ` · ${submission.project.project_name}` : ""}
        </div>
        <div className="text-text-muted text-[11px] font-mono mt-1">
          Briefed {fmtDateTime(submission.captured_at)}
        </div>
      </div>

      {/* Existing sign-ons */}
      <Card className="mb-4">
        <div className="p-4 border-b border-line flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted">Sign-ons</div>
            <div className="text-[13px] font-mono mt-0.5">
              {signons.length} / {minSign} min
              {metQuota && <span className="text-ok ml-2">· quota met</span>}
            </div>
          </div>
          <Pill label={metQuota ? "ready" : "need more"} tone={metQuota ? "approved" : "warning"} />
        </div>
        {signons.length === 0 ? (
          <EmptyState title="No one signed on yet" description="First up?" icon={UserPlus} />
        ) : signons.map((s, i) => (
          <div key={s.signon_id} className={`px-4 py-3 flex items-center gap-3 ${i < signons.length - 1 ? "border-b border-line" : ""}`}>
            <div className="w-9 h-9 rounded-full bg-accent/12 text-accent text-[11px] font-semibold flex items-center justify-center">
              {initials(s.signer_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-text">{s.signer_name}</div>
              <div className="text-[11px] text-text-muted font-mono">
                {s.signer_company ?? "—"}{s.signer_role ? ` · ${s.signer_role}` : ""} · {fmtDateTime(s.signed_at)}
              </div>
            </div>
            {(s.employee_id === user?.employee_id) && (
              <button onClick={() => removeMut.mutate(s.signon_id)} className="text-text-muted hover:text-danger p-1" title="Remove my sign-on">
                <Trash2 size={13}/>
              </button>
            )}
          </div>
        ))}
      </Card>

      {/* Sign-on form */}
      <Card>
        <div className="p-4 border-b border-line flex items-center justify-between">
          <h3 className="font-semibold">Sign on</h3>
          <GpsIndicator gps={gps} compact />
        </div>
        <div className="p-4 space-y-4">
          {!alreadySignedBySelf && (
            <div className="grid grid-cols-2 gap-2">
              {([
                ["self",  "I'm signing on"],
                ["other", "Someone else"],
              ] as const).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setPickingMode(mode)}
                  className={`px-3 py-2.5 rounded border text-[13px] font-medium transition-colors ${
                    pickingMode === mode
                      ? "bg-accent/12 border-accent/30 text-accent"
                      : "bg-ink-raised border-line text-text-muted hover:text-text"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {(pickingMode === "other" || alreadySignedBySelf) && (
            <>
              <Field label="Name">
                <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Full name" autoFocus />
              </Field>
              <Field label="Company">
                <Input value={signerCompany} onChange={e => setSignerCompany(e.target.value)} placeholder="e.g. ACME Electrical" />
              </Field>
              <Field label="Role">
                <Input value={signerRole} onChange={e => setSignerRole(e.target.value)} placeholder="e.g. Sparkie, Rigger, Traffic" />
              </Field>
            </>
          )}

          <Field label="Signature" hint="Sign with your finger on the grey area below.">
            <SignaturePad ref={sigRef} />
          </Field>

          {signMut.isError && (
            <div className="bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">
              {signMut.error instanceof Error ? signMut.error.message : "Couldn't save sign-on"}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex items-center justify-between">
          <div className="text-[11px] text-text-muted">
            By signing, you confirm you've read and understood the briefing.
          </div>
          <Button
            icon={CheckCircle2}
            onClick={() => signMut.mutate()}
            disabled={signMut.isPending || (pickingMode === "other" && !signerName.trim())}
          >
            {signMut.isPending ? "Saving…" : "Sign on"}
          </Button>
        </div>
      </Card>

      {metQuota && (
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={() => navigate("/forms")}>Done</Button>
        </div>
      )}
    </div>
  );
}

function initials(name: string | null | undefined): string {
  if (!name) return "?";
  return name.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}
